import React, { useState, useEffect } from "react";
import { Typography, Grid2 } from "@mui/material";
import Divider from "@mui/material/Divider";
import AppButton from "../../components/CommonReusableComponent/AppButton/AppButton";
import MUILoader from "../../components/MUILoader/MUILoader";
import MUITableSearch from "../../components/MUITable/MUITableSearch";
import AppInputField from "../../components/CommonReusableComponent/AppInputField/AppInputField";
// components
import UserMasterDataTable from "./components/UserMasterDataTable";
import CreateNewOrUpdateUserModal from "./components/CreateNewOrUpdateUserModal";
import DeleteUserConfirmationModal from "./components/DeleteUserConfirmationModal";
import { plantMasterColumnsData } from "./components/UserMaster.config";
import { handleOutSideDataTableSearch } from "../../common/MuiTableFunctions.config";
import { useSnackbar } from "../../contexts/Snackbar.context";
import {
  APP_MESSAGE,
  ERROR_MESSAGE,
  SNACKBAR_VARIANT,
} from "../../config/Messages.config";
import {
  deleteUser,
  getAllUsersByPlantIdWithoutSuperAdmin,
} from "../../helpers/api-functions-helper";
import { getUserRoleAndScreens } from "../../services/authServices/auth.services";
import { getDataForAllOption, getPageTitle, setRequiredDataForDropdown, sortDataAlphabetically } from "../../common/CommonFunctions";
import UserMasterDataTable_V2 from "./components/UserMasterDataTable_V2";
import { getRelativeRoleName, ROLES } from "../../config/Roles.config";
import AppAutoCompleteField from "../../components/CommonReusableComponent/AppAutoCompleteField/AppAutoCompleteField";
import { useBackdrop } from "../../contexts/BackDrop.context";

const UserMaster = () => {
  const documentTitle = getPageTitle() && getPageTitle();
  document.title = documentTitle ? `${documentTitle} | Zing Auditor` : "Zing Auditor"
  const { showSnackbar } = useSnackbar();
  const { assignedBranches, role, accountId } = getUserRoleAndScreens();
  const [isTableDataLoading, setIsTableDataLoading] = useState(false);
  const [selectPlant, setSelectPlant] = useState(null);
  const [allPlants, setAllPlants] = useState([]);
  // const [allRoles, setAllRoles] = useState([]);
  const [tableDataInstance, setTableDataInstance] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [toggleCreateOrUpdateModal, setToggleCreateOrUpdateModal] =
    useState(false);
  const [toggleDeleteModal, setToggleDeleteModal] = useState(false);
  const [isDeleteClicked, setIsDeleteClicked] = useState(false);
  const [modalDataState, setModalDataState] = useState(null);

  // search functionality
  const [searchText, setSearchText] = useState("");

  // All Object for selection
  const allObjWithNameId = [{ name: "All", id: "All" }];
  // const allObjWithLabelValue = [{ label: "All", value: "All" }];

  const is_SuperAdmin =
      role && role.toLowerCase() === ROLES.SUPERADMIN ? true : false;

  // Fetch all plants
  const fetchAllPlants = () => {
    const hasMultipleBranches = assignedBranches?.length > 1;
    const hasBranches = assignedBranches?.length > 0;
    
    const dataSet = is_SuperAdmin && hasMultipleBranches
    ? [...allObjWithNameId, ...assignedBranches]
    : assignedBranches || [];

    setRequiredDataForDropdown(setAllPlants, dataSet, "name", "id", ["isActive"]);

    const defaultPlantId =
      is_SuperAdmin && hasMultipleBranches
        ? "All"
        : hasBranches
        ? assignedBranches[0].id
        : null;

    setSelectPlant(defaultPlantId);
  };

  const getFilteredDataOfUsers = (DATA) => {
    const filteredData =
      DATA && DATA?.length > 0
        ? DATA.map((user) => ({
            ...user,
            assignedBranches:
              user.assignedBranches?.filter((branch) => branch.isActive) || [],
            assignedDepartments:
              user?.assignedBranches?.length > 0
                ? user?.assignedBranches[0]?.assignedDepartments || []
                : [],
            assignedDepartmentsForDataTable:
              user?.assignedBranches?.length > 0
                ? user?.assignedBranches[0]?.assignedDepartments
                    ?.map((dept) => dept?.name)
                    .join(", ") || []
                : [],
            branchName:
              user?.assignedBranches?.length > 0
                ? user?.assignedBranches[0]?.name || "Not Available"
                : "Not Available",
          })).filter((user) => {
            const isSuperAdmin = user.roleName.toLowerCase() === "super admin";
            const isAdmin = user.roleName.toLowerCase() === "admin";
            const loggedInUserRole = role?.toLowerCase();

            // Filter out Super Admins always
            if (isSuperAdmin) return false;

            // Filter out Admins only if logged-in user is also an Admin
            if (isAdmin && loggedInUserRole === "admin") return false;

            return true; // Keep all other users
          })
        : [];
    console.log("filteredData: ", filteredData)    

    return filteredData;
  };

  // Fetch all user of a plant/project
  const fetchAllUsersOfPlant = (data) => {
    const payload = {
      branchIds: getDataForAllOption("branch", data, allPlants),
      accountId: accountId,
    };
    setIsTableDataLoading(true);
    getAllUsersByPlantIdWithoutSuperAdmin(payload)
      .then((response) => {
        if (response && response.data) {
          const { statusCode, data, message } = response.data;
          if (statusCode && statusCode === 200) {
            const filteredData = getFilteredDataOfUsers(data)
            setTableDataInstance(filteredData);
            setTableData(filteredData);
          } else {
            showSnackbar(message, SNACKBAR_VARIANT.ERROR);
            setTableDataInstance(data);
            setTableData(data);
          }
        }
      })
      .catch((error) => {
        console.log("Error: ", error);
        showSnackbar(
          ERROR_MESSAGE.FAILED_TO_FETCH_DATA,
          SNACKBAR_VARIANT.ERROR
        );
      })
      .finally(() => {
        setIsTableDataLoading(false);
      });
  };

  // Function used toggle modal on their types
  const handleToggleModal = (modalType) => {
    if (modalType === "Create/Update") {
      setToggleCreateOrUpdateModal((e) => !e);
    } else {
      setToggleDeleteModal((e) => !e);
    }
  };

  const getRowDataOnClickOfAction = (actionName, data) => {
    if (actionName === "Update User") {
      setModalDataState(data);
      handleToggleModal("Create/Update");
    } else if (actionName === "Create User") {
      handleToggleModal("Create/Update");
    } else {
      setModalDataState(data);
      handleToggleModal("Delete User");
    }
  };

  const deleteExistingUser = () => {
    setIsDeleteClicked(true);
    deleteUser(modalDataState?.id)
      .then((res) => {
        if (res && res.data) {
          // console.log(res.data);
          const { statusCode, success } = res.data;
          if (!success && statusCode !== 200) {
            showSnackbar(
              APP_MESSAGE.USER_MASTER.DELETE.FAILED,
              SNACKBAR_VARIANT.ERROR
            );
          } else {
            showSnackbar(
              APP_MESSAGE.USER_MASTER.DELETE.SUCCESS,
              SNACKBAR_VARIANT.SUCCESS
            );
            handleToggleModal("Delete");
            setModalDataState(null);
            fetchAllUsersOfPlant(selectPlant);
          }
        }
      })
      .catch((error) => {
        console.log("Error: ", error);
        showSnackbar(
          APP_MESSAGE.PLANT_MASTER.DELETE.FAILED,
          SNACKBAR_VARIANT.ERROR
        );
      })
      .finally(() => {
        setIsDeleteClicked(false);
      });
  };

  // const handleChangePlant = (event) => {
  //   const { value } = event.target;
  //   setSelectPlant(value);
  // };
  const handleChangePlant = (Data) => {
    setSelectPlant(Data?.value || null);
  };

  useEffect(() => {
    if (navigator.onLine) {
      if (selectPlant) {
        fetchAllUsersOfPlant(selectPlant);
      }
    } else {
      showSnackbar(ERROR_MESSAGE.INTERNET_ERROR, SNACKBAR_VARIANT.ERROR);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectPlant]);

  useEffect(() => {
    fetchAllPlants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Outside Table search functionality
  useEffect(() => {
    if (searchText === "") {
      // Reset to original data if search text is cleared
      setTableData(tableDataInstance);
    } else {
      const filteredData_ = handleOutSideDataTableSearch(
        searchText,
        tableDataInstance
      );
      setTableData(filteredData_);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  return (
    <React.Fragment>
      {toggleCreateOrUpdateModal ? (
        <CreateNewOrUpdateUserModal
          ListOfAllPlants={allPlants || []}
          Open={toggleCreateOrUpdateModal}
          Title={modalDataState ? "Update Existing User" : "Create New User"}
          DialogData={modalDataState}
          SetDialogData={setModalDataState}
          ToggleDialogBox={handleToggleModal}
          FetchAllUserOfPlant={fetchAllUsersOfPlant}
          PlantId={selectPlant}
          AllUserNames={
            tableDataInstance[0] &&
            tableDataInstance.map((user) => user.userName.toLowerCase())
          }
        />
      ) : null}
      {toggleDeleteModal ? (
        <DeleteUserConfirmationModal
          ModalState={toggleDeleteModal}
          SetModalState={setModalDataState}
          ToggleFunction={handleToggleModal}
          LoaderState={isDeleteClicked}
          OnSubmit={deleteExistingUser}
        />
      ) : null}
      <div className="page-content">
        <Grid2 container className="d-flex align-items-center my-1" spacing={2}>
          <Grid2 size={{xs: 12, sm: 4, md: 6, lg: 6}} className="py-1">
            <Typography className="inter-medium-500">
              {documentTitle}
            </Typography>
          </Grid2>
          <Grid2 size={{xs: 12, sm: 5, md: 4, lg: 4}} className="py-1">
            {/* <AppInputField
              IsSelect={true}
              Name={"selectPlant"}
              Id={"selectPlant"}
              Value={selectPlant}
              SelectOptions={allPlants || []}
              Label={"Select branch"}
              OnChange={handleChangePlant}
            /> */}
            <AppAutoCompleteField
              IsMultiple={false}
              Id={"selectPlant"}
              TextInputName={"selectPlant"}
              Value={selectPlant}
              Options={allPlants || []}
              TextInputLabel={"Select branch"}
              TextInputPlaceholder={"Select branch"}
              HandleOnChange={(event, value) => {
                handleChangePlant(value);
              }}
            />
          </Grid2>
          <Grid2 size={{xs: 12, sm: 3, md: 2, lg: 2}} className="py-1">
            <AppButton OnPress={() => handleToggleModal("Create/Update")}>
              Create New User
            </AppButton>
          </Grid2>
        </Grid2>
        <Divider
          style={{ color: "GrayText", border: "0.02rem solid GrayText" }}
          className="mt-2"
        />
        {!isTableDataLoading ? (
          <Grid2 container className="my-2" spacing={2}>
            <Grid2 size={{xs: 12, sm: 4, md: 4, lg: 4}}>
              <MUITableSearch
                CancelSearch={() => {
                  setSearchText("");
                }}
                HandleSearch={(e) => setSearchText(e.target.value)}
                SearchText={searchText}
              />
            </Grid2>
          </Grid2>
        ) : null}

        <Grid2 container className="d-flex align-items-center my-2">
          <Grid2 size={{xs: 12, sm: 12, md: 12, lg: 12}}>
            {isTableDataLoading ? (
              <MUILoader />
            ) : (
              // <UserMasterDataTable
              //   Columns={plantMasterColumnsData}
              //   DenseTable={true}
              //   IsOutSideSearch={true}
              //   GetRowDataOnClickOfEditIcon={(actionName, data) => {
              //     getRowDataOnClickOfAction(actionName, data);
              //   }}
              //   RowsToDisplay={10}
              //   RowData={tableData || []}
              //   OptionsForRowsPerPage={[5, 10, 15, 25, 50]}
              // />
     
              <UserMasterDataTable_V2
                Rows={tableData || []}
                GetRowDataOnClickOfAction={getRowDataOnClickOfAction}
              />
            )}
          </Grid2>
        </Grid2>
      </div>
    </React.Fragment>
  );
};

export default UserMaster;
